import type {
  CreateInvoiceData,
  Invoice,
  InvoiceStatusUpdate,
  UpdateInvoiceData,
} from '../types/invoice'
import type { Paginated } from '../types/pagination'
import type { Tag } from '../types/tag'
import type { WorkOrder } from '../types/workorder'
import { api } from './fetch'

export const invoiceService = {
  create: async (data: CreateInvoiceData): Promise<Invoice> => {
    const response = await api.post('/invoice/', data)
    return response.data
  },

  list: async (params?: {
    page?: number
    page_size?: number
    status?: string
  }): Promise<Paginated<Invoice>> => {
    const usp = new URLSearchParams()
    if (params?.page) usp.append('page', String(params.page))
    if (params?.page_size) usp.append('page_size', String(params.page_size))
    if (params?.status) usp.append('status', params.status)
    const qs = usp.toString()
    const response = await api.get<Paginated<Invoice> | Invoice[]>(
      `/invoice/${qs ? `?${qs}` : ''}`,
    )
    const data = response.data as unknown
    if (Array.isArray(data)) {
      // Fallback for non-paginated backend responses
      const items = data as Invoice[]
      const total = items.length
      const currentPage = Math.max(1, params?.page ?? 1)
      const size = Math.max(1, Math.min(params?.page_size ?? total, total))
      const start = (currentPage - 1) * size
      const paged = items.slice(start, start + size)
      const pages = size > 0 ? Math.max(1, Math.ceil(total / size)) : 1
      return { items: paged, count: total, page: currentPage, pages }
    }
    return data as Paginated<Invoice>
  },

  get: async (id: number): Promise<Invoice> => {
    const response = await api.get(`/invoice/${id}/`)
    return response.data
  },

  update: async (
    id: number,
    invoiceData: UpdateInvoiceData,
  ): Promise<Invoice> => {
    const response = await api.put(`/invoice/${id}/`, invoiceData)
    return response.data
  },

  delete: async (id: number): Promise<{ success: boolean }> => {
    const response = await api.delete(`/invoice/${id}/`)
    return response.data
  },

  getDriverObjects: async (params?: {
    workorder_id?: number
    assigned_user_id?: number
  }): Promise<Tag[]> => {
    const usp = new URLSearchParams()
    if (params?.workorder_id)
      usp.append('workorder_id', String(params.workorder_id))
    if (params?.assigned_user_id)
      usp.append('assigned_user_id', String(params.assigned_user_id))
    const qs = usp.toString()
    const response = await api.get(
      `/invoice/driver-objects/${qs ? `?${qs}` : ''}`,
    )
    return response.data
  },

  getBrokerObjects: async (params?: {
    project_id?: number
    workorder_id?: number
  }): Promise<Tag[]> => {
    const usp = new URLSearchParams()
    if (params?.project_id) usp.append('project_id', String(params.project_id))
    if (params?.workorder_id)
      usp.append('workorder_id', String(params.workorder_id))
    const qs = usp.toString()
    const response = await api.get(
      `/invoice/broker-objects/${qs ? `?${qs}` : ''}`,
    )
    return response.data
  },

  getContractorObjects: async (params?: {
    project_id?: number
    workorder_id?: number
  }): Promise<Tag[]> => {
    const usp = new URLSearchParams()
    if (params?.project_id) usp.append('project_id', String(params.project_id))
    if (params?.workorder_id)
      usp.append('workorder_id', String(params.workorder_id))
    const qs = usp.toString()
    const response = await api.get(
      `/invoice/contractor-objects/${qs ? `?${qs}` : ''}`,
    )
    return response.data
  },

  downloadPdf: async (id: number): Promise<Blob> => {
    const response = await api.get(`/invoice/${id}/download/`, {
      responseType: 'blob',
    })
    return response.data
  },

  downloadCsv: async (id: number): Promise<Blob> => {
    const response = await api.get(`/invoice/${id}/csv/`, {
      responseType: 'blob',
    })
    return response.data
  },

  received: async (params?: {
    page?: number
    page_size?: number
    status?: string
  }): Promise<Paginated<Invoice>> => {
    const usp = new URLSearchParams()
    if (params?.page) usp.append('page', String(params.page))
    if (params?.page_size) usp.append('page_size', String(params.page_size))
    if (params?.status) usp.append('status', params.status)
    const qs = usp.toString()
    const response = await api.get<Paginated<Invoice> | Invoice[]>(
      `/invoice/received/${qs ? `?${qs}` : ''}`,
    )
    const data = response.data as unknown
    if (Array.isArray(data)) {
      const items = data as Invoice[]
      const total = items.length
      const currentPage = Math.max(1, params?.page ?? 1)
      const size = Math.max(1, Math.min(params?.page_size ?? total, total))
      const start = (currentPage - 1) * size
      const paged = items.slice(start, start + size)
      const pages = size > 0 ? Math.max(1, Math.ceil(total / size)) : 1
      return { items: paged, count: total, page: currentPage, pages }
    }
    return data as Paginated<Invoice>
  },

  updateStatus: async (
    id: number,
    statusData: InvoiceStatusUpdate,
  ): Promise<Invoice> => {
    const response = await api.patch(`/invoice/${id}/status/`, statusData)
    return response.data
  },

  disputeInvoice: async (id: number, reason: string): Promise<Invoice> => {
    const response = await api.post(`/invoice/${id}/dispute/`, { reason })
    return response.data
  },

  clearDispute: async (id: number): Promise<Invoice> => {
    const response = await api.post(`/invoice/${id}/clear-dispute/`)
    return response.data
  },
}
